import React from "react";
import { useMemo } from "react";
import {
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    Stack,
    Toolbar,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PDFPreviewModal from "./PDFPreviewModal";
import PrescriptionView from "./views/PrescriptionView";
import ConsultationView from "./views/ConsultationView";
import HealthDocumentView from "./views/HealthDocumentView";
import DiagnosticStudiesView from "./views/DiagnosticStudiesView";
import DischargeSummaryView from "./views/DischargeSummaryView";
import ImmunizationView from "./views/ImmunizationView";
import WellnessRecordView from "./views/WellnessRecordView";
import InvoiceView from "./views/InvoiceView";
import { labelSx, sectionTitleSx } from "./styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Transition = React.forwardRef(function Transition(props: any, ref: any) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function FhirBundleModal({
    open,
    onClose,
    bundles = [],
    currentIndex = 0,
    onIndexChange = () => {},
    loading = false,
    onRetrigger,
    retriggering = false,
}: {
    open: boolean;
    onClose: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bundles?: any[];
    currentIndex?: number;
    onIndexChange?: (idx: number) => void;
    loading?: boolean;
    onRetrigger?: () => void;
    retriggering?: boolean;
}) {
    const [showPdfPreview, setShowPdfPreview] = React.useState(false);
    const total = bundles?.length || 0;
    const bundle = useMemo(() => (Array.isArray(bundles) ? bundles[currentIndex] : null), [bundles, currentIndex]);
    const bundleType = bundle?.composition?.bundleType || bundle?.composition?.typeText || bundle?.bundleType || "FHIR Bundle";
    // Walk the bundle-type candidates and skip sentinel values like "Unknown"
    // (legacy backends emit this when they fail to classify a bundle). This
    // keeps the modal from falling all the way through to the raw JSON view
    // just because the root bundleType happens to be a stale sentinel.
    const UNCLASSIFIED = ["", "unknown", "null", "undefined", "n/a", "na"];
    const typeCandidates = [
        bundle?.composition?.bundleType,
        bundle?.bundleType,
        bundle?.composition?.typeText
    ];
    const normalizedType = (typeCandidates
        .map((c) => (c || "").toString().trim().toLowerCase())
        .find((c) => c && !UNCLASSIFIED.includes(c))) || "";

    // Resource-shape fallback: when the backend populated one of the
    // structured payloads (e.g. bundle.invoice) the bundle IS that type
    // regardless of what the classifier said. Mirrors recordTypeMap.js.
    const hasInvoicePayload = Boolean(bundle?.invoice);
    const hasPrescriptionPayload = Array.isArray(bundle?.medicationRequest) && bundle.medicationRequest.length > 0;
    const hasImmunizationPayload = Array.isArray(bundle?.processedImmunization) && bundle.processedImmunization.length > 0;
    const hasDiagnosticPayload = Array.isArray(bundle?.diagnosticReport) && bundle.diagnosticReport.length > 0;

    const isPrescription = normalizedType.includes("prescription") || hasPrescriptionPayload;
    const isConsultation = normalizedType.includes("consultation") || normalizedType.includes("consult") || normalizedType.includes("opconsult");
    const isHealthDocument = normalizedType.includes("health document");
    const isDiagnosticStudies = normalizedType.includes("diagnostic") || hasDiagnosticPayload;
    const isDischargeSummary = normalizedType.includes("discharge");
    const isWellnessRecord = normalizedType.includes("wellness");
    const isImmunization = normalizedType.includes("immunization") || hasImmunizationPayload;
    const isInvoice = normalizedType.includes("invoice") || hasInvoicePayload;

    const goPrev = () => onIndexChange(Math.max(currentIndex - 1, 0));
    const goNext = () => onIndexChange(Math.min(currentIndex + 1, total - 1));

    const renderContent = () => {
        if (!bundle) return null;
        // Invoice is checked before HealthDocument because an Invoice bundle
        // carrying a PDF DocumentReference attachment can get stamped
        // "Health Document Record" by a stale backend; the resource-shape
        // fallback above rescues it and this ordering makes sure the rescue
        // routes to InvoiceView instead of the generic document viewer.
        if (isPrescription) return <PrescriptionView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isInvoice) return <InvoiceView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isConsultation) return <ConsultationView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isDiagnosticStudies) return <DiagnosticStudiesView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isDischargeSummary) return <DischargeSummaryView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isImmunization) return <ImmunizationView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isWellnessRecord) return <WellnessRecordView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        if (isHealthDocument) return <HealthDocumentView bundle={bundle} setShowPdfPreview={setShowPdfPreview} />;
        return (
            <Box>
                <Typography sx={sectionTitleSx}>Unsupported bundle type</Typography>
                <Typography sx={labelSx}>Showing raw payload for debugging.</Typography>
                <Box component="pre" sx={{ mt: 1, p: 2, bgcolor: "grey.100", color: "text.primary", borderRadius: 1, overflow: "auto" }}>
                    {JSON.stringify(bundle, null, 2)}
                </Box>
            </Box>
        );
    };

    return (
        <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
            <AppBar
                position="relative"
                sx={{
                    background: "linear-gradient(135deg, #2fbf71 0%, #27a864 100%)",
                    boxShadow: "0 8px 32px rgba(47,191,113,0.35)",
                    borderBottom: "3px solid rgba(255,255,255,0.15)"
                }}
            >
                <Toolbar sx={{ py: 2, px: 3 }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                        sx={{
                            mr: 2,
                            transition: "all 0.3s ease",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,0.15)",
                                transform: "scale(1.05)"
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: 0.8,
                                fontSize: "1.2rem",
                                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                mb: 0.5
                            }}
                        >
                            FHIR Bundle Viewer
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                opacity: 0.9,
                                fontSize: 13,
                                fontWeight: 500,
                                letterSpacing: 0.3
                            }}
                        >
                            {`Record ${total === 0 ? 0 : currentIndex + 1} of ${total}`}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            color="inherit"
                            startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                            disabled={currentIndex === 0 || total === 0}
                            onClick={goPrev}
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                letterSpacing: 0.4,
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                bgcolor: "rgba(255,255,255,0.15)",
                                "&:hover:not(:disabled)": {
                                    bgcolor: "rgba(255,255,255,0.25)",
                                    transform: "translateX(-2px)"
                                },
                                "&:disabled": {
                                    opacity: 0.5,
                                    cursor: "not-allowed"
                                }
                            }}
                        >
                            Prev
                        </Button>
                        <Button
                            color="inherit"
                            endIcon={<ArrowForwardIosIcon fontSize="small" />}
                            disabled={currentIndex >= total - 1 || total === 0}
                            onClick={goNext}
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                letterSpacing: 0.4,
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                bgcolor: "rgba(255,255,255,0.15)",
                                "&:hover:not(:disabled)": {
                                    bgcolor: "rgba(255,255,255,0.25)",
                                    transform: "translateX(2px)"
                                },
                                "&:disabled": {
                                    opacity: 0.5,
                                    cursor: "not-allowed"
                                }
                            }}
                        >
                            Next
                        </Button>
                    </Stack>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ bgcolor: "#f8f9fa", p: 3 }}>
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
                        <CircularProgress sx={{ color: "#2fbf71" }} />
                    </Box>
                ) : total === 0 || !bundle ? (
                    <Box sx={{ textAlign: "center", color: "text.secondary", mt: 8 }}>
                        <Typography variant="h6" gutterBottom>No FHIR data found for this artefact.</Typography>
                        <Typography variant="body2" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                            The health records may not have been received yet. Click "Re-fetch data" to ask the HIP to push the records again.
                        </Typography>
                        {onRetrigger && (
                            <Button
                                variant="contained"
                                onClick={onRetrigger}
                                disabled={retriggering}
                                startIcon={retriggering ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined}
                                sx={{ bgcolor: "#2fbf71", "&:hover": { bgcolor: "#27a864" }, fontWeight: 700 }}
                            >
                                {retriggering ? "Re-fetching… (wait ~60s)" : "Re-fetch data"}
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                mb: 4,
                                p: 3,
                                bgcolor: "#ffffff",
                                borderRadius: 3,
                                border: "2px solid #e0f2f1",
                                boxShadow: "0 6px 24px rgba(129,196,187,0.15)"
                            }}
                        >
                            <Chip
                                label={bundleType}
                                sx={{
                                    background: "#2fbf71",
                                    color: "#ffffff",
                                    fontWeight: 800,
                                    fontSize: "1rem",
                                    letterSpacing: 0.8,
                                    textTransform: "uppercase",
                                    border: "none",
                                    px: 2,
                                    py: 1.5,
                                    height: "auto",
                                    borderRadius: 2,
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "& .MuiChip-label": {
                                        px: 0,
                                        py: 0
                                    }
                                }}
                            />
                            {bundle.transactionId ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            color: "#8893a2",
                                            fontWeight: 700,
                                            letterSpacing: 0.8,
                                            textTransform: "uppercase",
                                            mb: 0.25
                                        }}
                                    >
                                        Transaction ID
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: "#1f3a2e",
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: 320
                                        }}
                                    >
                                        {bundle.transactionId}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Stack>
                        {renderContent()}
                    </Box>
                )}
            </DialogContent>

            <PDFPreviewModal open={showPdfPreview} onClose={() => setShowPdfPreview(false)} pdfData={bundle?.binary?.data} />
        </Dialog>
    );
}
